import * as React from 'react';
import { useCallback, useState } from 'react';
import { useLoaderData } from "react-router";
import { Outlet, useNavigate,
    Link as RouterLink, 
    LinkProps as RouterLinkProps
} from "react-router-dom";
import AddIcon from '@mui/icons-material/Add';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemProps, ListItemSecondaryAction, 
    ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import AppBarPage from "../components/AppBarPage";
import ClientEditor from '../components/ClientEditor';
import AccessEditor from '../components/AccessEditor';
import StickyFab from '../components/StickyFab';
import useDialog from '../hooks/useDialog';
import { Client } from '../models/core';
import { AuthData, Practice } from '../models/ziphy';
import { API } from '../utils/Api';
import { API as ZiphyAPI } from '../utils/ZiphyAPI';
import { analytics } from '../utils/analytics/zipAnalytics';
import { useLoading } from '../hooks/useLoading';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import LinkIcon from '@mui/icons-material/Link';
import useSnackbar from '../hooks/useSnackbar';
import AlertSnack from '../components/ErrorSnack';


export const loader = async (): Promise<[Client[], boolean]> => {
    const authItem = window.localStorage.getItem('auth');
    const auth = authItem ? (JSON.parse(authItem) as AuthData) : null;
    API.token = auth?.accessToken?.value || '';
    ZiphyAPI.token = API.token;

    const isAdmin = auth?.roles?.find(r => r.role == 'admin') != undefined;
    if (isAdmin) {
        return [(await API.getClients(undefined, [['name', 1]])).items, isAdmin];
    }

    const seenPracticeIds = new Set();
    (auth?.roles || []).forEach(r => {
        if ((r.role == 'practice_admin' || r.role == 'dispatcher')) {
            if(r.serviced_practice_ids) {
                r.serviced_practice_ids?.forEach(e => {
                    if(!seenPracticeIds.has(e)) {
                        seenPracticeIds.add(e);
                    }
                })
            } else if(!seenPracticeIds.has(r.practice_id)) {
                seenPracticeIds.add(r.practice_id);
            }
        }
    });

    const practiceArray = Array.from(seenPracticeIds).map((id) => id + '');
    const filter = { practiceId: { $in: practiceArray } }

    const clients =(await API.getClients(filter).catch((error) => {
            console.error(`Error Retrieving Clients: ${error}`)
            analytics.error('client_auth_error', {message:error}, true);
        }))?.items || [];
    return [clients, isAdmin];
}

const Link = React.forwardRef<HTMLAnchorElement, RouterLinkProps>(function Link(itemProps, ref) {
    return (
        <RouterLink ref={ref} {...itemProps} role={undefined} style={{ color: 'inherit' }} />
    );
});

interface ClientListItemProps extends ListItemProps<typeof RouterLink> {
    client:Client;
    to:string;
    onMore?: (client:Client, event: HTMLElement, rootUrl?: string) => void;
}

const ClientListItem = ({ client:item, to='', onMore, ...props }:ClientListItemProps): JSX.Element => {
    const loading = useLoading();
    const snackBar = useSnackbar()

    const onMoreClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        onMore && onMore(item, event.currentTarget)
        event.stopPropagation();
    }, [item, onMore]);

    const onClientClick = useCallback(() => {
        loading.showLoading();
        analytics.breadcrumb("client_select", { clientId: item._id});
    }, [item, loading]);

    /*const onCampaignsClick = useCallback(() => {
        loading.showLoading();
        analytics.breadcrumb("campaigns_select", { clientId: item._id});
    }, [item, loading]);*/

    return (
        <ListItem key={item._id}
            component={Link}
            to={to || item._id + '/patients?status=Active'} 
            onClick={onClientClick}
            hideMenu={true}
            {...props}>
            <ListItemButton>
                <ListItemIcon>
                    <ListAltIcon />
                </ListItemIcon>
                    <ListItemText
                        primary={item.name}
                        primaryTypographyProps={{
                            sx: {
                            width: '80%'
                            }
                        }}
                    />
            </ListItemButton>
            <ListItemSecondaryAction>
                <Tooltip id="tt-patients" title="Patients">
                    <IconButton onClick={onClientClick}>
                        <PeopleAltIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip id='tt-copy-referal' title='Copy Public Referal Page'>
                    <IconButton onClick={(e) => {
                        const host = window.location.hostname == 'localhost' ? window.location.host : window.location.hostname;
                        const url = `${window.location.protocol}//${host}/p/referals/${item._id.toString()}`;
                        navigator.clipboard.writeText(url);
                        e.stopPropagation();
                        e.preventDefault();
                        snackBar.showSuccess(`Public Referal Link Copied: ${url}`);
                    }}>
                        <LinkIcon/>
                    </IconButton>
                </Tooltip>
                {
                /*<Tooltip id="tt-campaigns" title="Campaigns">
                    <Link to={item._id + '/campaigns'}>
                        <IconButton onClick={onCampaignsClick}>
                            <FlagIcon />
                        </IconButton>
                    </Link>
                </Tooltip>*/
                }
                <Tooltip id="tt-more-options" title="More Options">
                    <IconButton onClick={onMoreClick}>
                        <MoreVertIcon />
                    </IconButton>
                </Tooltip>
            </ListItemSecondaryAction>
            <AlertSnack {...snackBar}/>
        </ListItem>
    )
}

const ClientsPage = (): JSX.Element => {

    const [items, isAdmin] = useLoaderData() as [Client[], boolean];
    const [practices, setPractices] = useState<Practice[]|null>(null);
    const [clientToEdit, setClientToEdit] = useState<Client | null>(null);
    const [duplicate, setDuplicate] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const {hide: hideCE, open: ceOpen, show: shoeCE} = useDialog();
    const {hide: hideAE, open: aeOpen, show: showAE} = useDialog();
    const navigate = useNavigate();
    const loading = useLoading();

    React.useEffect(() => {loading.hideLoading();}, [])

    const requestPractices = useCallback(async (onComplete:(success:boolean)=>void) => {
        loading.showLoading();
        let practiceState = practices;
        if(practices == null) {
            // TODO: Change to use query instead, design must change to prevent dialog crashes and last popin
            const req = await ZiphyAPI.getPractices().catch(error => { console.log(error); });
            practiceState = req || [];
            setPractices(practiceState);
        }
        onComplete && onComplete(practiceState != undefined && practiceState != null)
        loading.hideLoading();
    },[practices, loading]);

    const onMore = useCallback((client: Client, target: HTMLElement) => {
        setClientToEdit(client);
        setAnchorEl(target);
    }, []);

    const onMenuClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const onCreateClick = useCallback(async () => {
        requestPractices(() => {
            setDuplicate(false);
            setClientToEdit(null);
            shoeCE();
        });
    }, [shoeCE, requestPractices]);

    const onEditClick = useCallback(async () => {
        requestPractices(() => {
            setAnchorEl(null);
            setDuplicate(false);
            shoeCE();
        });
    }, [shoeCE, requestPractices]);

    const onDuplicateClick = useCallback(async () => {
        requestPractices(() => {
            setAnchorEl(null);
            setDuplicate(true);
            shoeCE();
        })
    }, [shoeCE, requestPractices]);

    const onAccesEditClick = useCallback(async () => {
        requestPractices(() => {
            setAnchorEl(null);
            setDuplicate(false);
            showAE();
        });
    }, [showAE, requestPractices]);

    const onSave = useCallback(() => {
        hideCE();
        navigate('.');
    }, [hideCE, navigate]);

    const onSaveUser = useCallback(() => {
        hideAE();
        navigate('.');
    }, [hideAE, navigate]);

    return (
        <AppBarPage title='Clients'>
            <List>
                {[...items.map((item) => (
                    <ClientListItem key={item._id} to={item._id} client={item} onMore={onMore} />
                )),
                ...(items.length == 0 ? [
                    <ListItem key='no-items-msg' sx={{
                        height: '102px',
                        display: 'flex',
                        justifyContent: 'center',
                        background: '#1976d210',
                        color: '#1976d2'
                    }}>
                        There is no clients now. Lets create one!
                    </ListItem>
                ] : [])
                ]}
            </List>
            <StickyFab onClick={onCreateClick} title='Create New Client'>
                <AddIcon />
            </StickyFab>
            {
            ceOpen ? <ClientEditor
                open={ceOpen}
                onClose={hideCE}
                client={clientToEdit || undefined}
                clientList={items}
                duplicate={duplicate}
                practices={(practices||[]).filter((p) => isAdmin || (items||[]).find((f) => f.practiceId == p.id))}
                onSave={onSave}
            /> : <></>
            }
            {
            aeOpen ? <AccessEditor 
                open={aeOpen}
                onClose={hideAE}
                onSave={onSaveUser}
                client={clientToEdit||undefined}
                practices={practices||[]}
                /> : <></>
            }
            <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={onMenuClose}
            >
                <MenuItem onClick={onEditClick}>Edit</MenuItem>
                <MenuItem onClick={onDuplicateClick}>Duplicate</MenuItem>
                <MenuItem onClick={onAccesEditClick}>Users</MenuItem>
            </Menu>
            <Outlet />
        </AppBarPage>
    );
}

export default ClientsPage;